import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Spinner } from "@/components/ui/spinner";
import { NavLink, useParams } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import type { Creator, Draft } from "@/types/types";
import DraftCard from "@/components/dashboard_campaigns/draft-card";
import { FileVideo } from "lucide-react";
import { useAuth } from "@clerk/react";
import { useCampaignStore, useCreatorsStore, useDraftStore } from "@/stores";
import EmptyState from "@/components/ui/empty-state";

export default function DraftsTab() {
  const { creator_id } = useParams();
  const { campaignId } = useCampaignStore();
  const { getToken } = useAuth();
  const {
    creators,
    isLoading: creatorsLoading,

    fetchCreators,
  } = useCreatorsStore();

  const { setActiveDraftCreator, updateDraft } = useDraftStore();

  // State management
  const [foundCreator, setFoundCreator] = useState<Creator | null>(null);
  const [draftCreators, setDraftCreators] = useState<Creator[]>([]);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [revisionNotes, setRevisionNotes] = useState("");
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    isVideo: boolean;
  } | null>(null);

  // Filter creators with drafts
  useEffect(() => {
    if (creators.length > 0) {
      const tempDraftCreators = creators.filter(
        (creator) => Array.isArray(creator.drafts) && creator.drafts.length > 0
      );
      setDraftCreators(tempDraftCreators);
    }
  }, [creators]);

  // Set active creator when creator_id changes
  useEffect(() => {
    if (creator_id && creators.length > 0) {
      const tempFoundCreator = creators.find(
        (creator) => creator._id === creator_id
      );
      setActiveDraftCreator(tempFoundCreator || null);
      setFoundCreator(tempFoundCreator || null);
    }
  }, [creator_id, creators, setActiveDraftCreator]);

  // Helper functions
  const isVideo = (url: string) => {
    return (url.match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|quicktime)($|\?)/i) !== null);
  };

  const openRevisionDialog = (draft: Draft) => {
    setSelectedDraft(draft);
    setRevisionNotes(draft.notes || "");
    setRevisionDialogOpen(true);
  };

  const openMediaPreview = (url: string) => {
    setPreviewMedia({ url, isVideo: isVideo(url) });
    setPreviewDialogOpen(true);
  };

  const handleUpdateDraft = async (
    field: string,
    newValue: string,
    draft: Draft,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true);
    const token = await getToken();

    try {
      if (token && creator_id) {
        await updateDraft(creator_id, draft.id, field, newValue, token);
      }

      if (campaignId && token) {
        await fetchCreators(campaignId, token);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error(`Error updating draft ${field} to ${newValue}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const submitRevision = async () => {
    if (!selectedDraft || !creator_id) return;

    setSubmittingRevision(true);

    try {
      const token = await getToken();
      if (!token) {
        console.error("No token found");
        return;
      }

      await updateDraft(
        creator_id,
        selectedDraft.id,
        "notes",
        revisionNotes,
        token
      );

      if (campaignId) {
        await fetchCreators(campaignId, token);
      } else {
        window.location.reload();
      }

      setRevisionDialogOpen(false);
      setSelectedDraft(null);
      setRevisionNotes("");
    } catch (error) {
      console.error("Error submitting revision:", error);
    } finally {
      setSubmittingRevision(false);
    }
  };

  // Loading state
  if (creatorsLoading && draftCreators.length === 0) {
    return <div className="w-full h-full flex items-center justify-center">
      <Spinner className="size-4" />
    </div>;
  }

  return (
    <div className="w-full h-full flex">
      {/* Sidebar creator list */}
      <div className="w-72 border-r flex flex-col h-full">
        <ScrollArea className="w-full h-full bg-background">
          {draftCreators.length > 0 ? (
            draftCreators.map((creator) => (
              <NavLink
                key={creator._id}
                className={`flex items-center gap-2 p-2 border-b cursor-pointer duration-75 transition-all 
                  ${
                    creator_id === creator._id
                      ? "text-primary font-medium border-l-4 border-l-primary"
                      : "hover:bg-muted"
                  }`}
                to={`/dashboard/campaigns/${campaignId}/drafts/${creator._id}`}
              >
                <Avatar className="size-10">
                  <AvatarImage src={creator.avatar || "/placeholder.svg"} />
                  <AvatarFallback>
                    {creator.username
                      ? creator.username.substring(0, 2).toUpperCase()
                      : "CN"}
                  </AvatarFallback>
                </Avatar>
                <div className="truncate w-full flex flex-col items-start">
                  <span className="text-sm">{creator.username}</span>
                  <span className="text-xs text-muted-foreground">
                    {creator.drafts?.length || 0}{" "}
                    {creator.drafts?.length === 1 ? "video" : "videos"}
                  </span>
                </div>
              </NavLink>
            ))
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              No creators with drafts found
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Main content area */}
      <div className="flex-1 h-full border-l-0 border-t-0 p-5 bg-background overflow-auto">
        {creator_id && foundCreator ? (
          <div className="space-y-6">
            {foundCreator.drafts && foundCreator.drafts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {foundCreator.drafts.map((draft: Draft) => (
                  <DraftCard
                    key={draft.id}
                    draft={draft}
                    openMediaPreview={(url) => url && openMediaPreview(url)}
                    openRevisionDialog={openRevisionDialog}
                    handleUpdateDraft={handleUpdateDraft}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center p-8">
                <EmptyState icon={<FileVideo />} text="No drafts available for this creator" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <EmptyState
              icon={<FileVideo/>}
              text="Select a creator to view their drafts"
            />
          </div>
        )}
      </div>

      {/* Revision Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent className="max-w-3xl p-5">
          <DialogHeader>
            <DialogTitle>Request Revision</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Revision Notes
            </label>
            <Textarea
              placeholder="Provide detailed feedback for the creator..."
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              rows={5}
              className="w-full h-64"
              disabled={submittingRevision}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevisionDialogOpen(false)}
              disabled={submittingRevision}
            >
              Cancel
            </Button>
            <Button onClick={submitRevision} disabled={submittingRevision}>
              {submittingRevision ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Submitting...
                </>
              ) : (
                "Submit Revision Request"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-5 pt-12 overflow-hidden">
          <div className="relative w-full h-full">
            {previewMedia && previewMedia.isVideo ? (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="w-full h-full max-h-[80vh] object-contain"
              />
            ) : (
              previewMedia && (
                <img
                  src={previewMedia.url || "/placeholder.svg"}
                  alt="Preview"
                  className="w-full max-h-[80vh] object-contain"
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
